/**
 * Stars — the rating control. Five stars, tapped in halves.
 *
 * This replaces the notched gem track. The constraints that survived the change
 * are the ones that were never about the slider:
 *
 *  - DISCRETE. Ten positions, half a star apart. Still a ladder with rungs, not
 *    a continuum: nothing here can produce a 3.47, and the person is choosing
 *    between ten things rather than sliding along a line.
 *  - NO DEFAULT POSITION. An unset control shows five empty stars and says
 *    "Not rated". A control that starts at the midpoint measurably anchors the
 *    rating that follows, so nothing here emits on mount or on focus; the only
 *    paths that call `onChange` are a tap, a drag and a key.
 *  - TAP ANYWHERE. Drag-only would fail WCAG 2.5.7 (Dragging Movements, AA).
 *    Every target is a whole 44 CSS px star: a tap sets that star full, and a
 *    second tap on the same one drops it to a half. Halves used to be picked by
 *    which side of a star the finger landed on, which made every target 22px —
 *    fine with a mouse, not fine with a thumb on a phone going round a table.
 *  - ONE MOVE, ONE `onChange`. A gesture across the row emits every star it
 *    crosses, because the fill and the readout have to follow the finger.
 *    Deciding which of those the rater meant is the consumer's job — see
 *    `createSettler` in the rating screen. What may be emitted at all is decided
 *    in one place, `planCommit`, so a value that did not change is not an
 *    emission and N/A only moves when it moves.
 *  - NATIVE `input[type=range]` underneath, so keyboard interaction and screen
 *    reader semantics come for free rather than being reimplemented badly. It is
 *    `pointer-events: none` and invisible: pointers are handled here, because
 *    the native thumb-inset mapping and a half-star hit grid disagree about
 *    which position a given x belongs to, and a tap and a drag landing on
 *    different values at the same x is the kind of bug nobody reports and
 *    everybody feels.
 *
 * `starView`, `planCommit`, `starIndexAt` and `tapValue` are pure and exported.
 * Every decision this component makes lives in one of them, so the emission rule,
 * the tap cycle and blind concealment are all assertable without a DOM.
 */

import { useCallback, useId, useRef } from "react";
import type {
  ChangeEvent as ReactChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from "react";
import {
  STAR_COUNT,
  STAR_MAX,
  STAR_MIN,
  clampStar,
  formatStars,
  fullValue,
  starsAnnouncement,
  starsOf,
} from "../../shared/stars.ts";

export interface StarsProps {
  /** Current rating, 1..10 half-stars, or null when nothing has been chosen. */
  value: number | null;
  /**
   * The half-star under the finger *now*. Fires on every position a gesture
   * crosses, and is not a commit — see the note on emissions above and
   * `createSettler` in `screens/session/Rating.tsx`.
   */
  onChange?: (value: number) => void;
  /** Named in `aria-label` and shown above the stars. */
  aspectName: string;
  /** Named in `aria-label`; a grid of these needs the rater in the label. */
  playerName?: string;
  /** Blind mode: other players' ratings are absent until the reveal. */
  blind?: boolean;
  /** True when this rating belongs to the person holding the device. */
  isOwn?: boolean;
  /** Set once every present participant has committed. */
  revealed?: boolean;
  /**
   * This seat has an answer on the table that this screen may not read back —
   * the pass-the-phone case, where a value belongs to whoever tapped the name
   * and not to whoever is holding the device now.
   *
   * It changes one thing: what the readout says. An empty control that says
   * "Not rated" to somebody who *has* rated is not a blank slate, it is a wrong
   * answer, and the natural response to it is to rate again — which is how a
   * committed answer gets silently written over. The value stays hidden.
   */
  committed?: boolean;
  disabled?: boolean;
  /**
   * Showing a number this rater did not type, and refusing the first gesture.
   *
   * The topic rows work themselves out from their follow-ups, so the stars can
   * be sitting on a figure nobody chose — and a live control under a figure
   * nobody chose is one thumb-brush away from replacing it with whatever the
   * finger landed on, silently, on a phone going round a table in a pub. So the
   * first gesture arms it and does nothing else; the second sets a value. Two
   * deliberate taps is the smallest gate that a stray one cannot pass.
   *
   * Not `disabled`: a disabled control is one you may not use, this is one you
   * have not picked up yet. It keeps its focus, its keyboard and its readout,
   * and the difference is said on screen rather than left to the cursor.
   */
  locked?: boolean;
  /** Raised by the gesture `locked` swallowed, so the consumer can unlock. */
  onArm?: () => void;
  /** Offers the "doesn't apply" escape hatch instead of a forced rating. */
  naEnabled?: boolean;
  na?: boolean;
  onNaChange?: (na: boolean) => void;
}

// ---------------------------------------------------------------------------
// The view model. Pure, because concealment has to be provable.
// ---------------------------------------------------------------------------

export interface StarViewInput {
  value: number | null;
  na?: boolean;
  blind?: boolean;
  isOwn?: boolean;
  revealed?: boolean;
  committed?: boolean;
}

export interface StarView {
  /** True while this is somebody else's rating, before the reveal. */
  concealed: boolean;
  /**
   * This seat's own answer is in and this screen may not show it. Distinct from
   * `concealed`, which is somebody else's: the control is still live, because a
   * rater is allowed to change their mind — it just no longer claims they never
   * answered.
   */
  sealed: boolean;
  /** The seated half-star, or null. Always null while concealed. */
  value: number | null;
  /** What the native input carries. Unset or concealed carries the floor. */
  inputValue: number;
  unset: boolean;
  /** Also the `aria-valuetext`, so it must never spell out a concealed rating. */
  readout: string;
  /** How much of the five-star row is filled, 0..100. */
  fill: number;
}

/**
 * In the order the answers rank: concealed beats n/a beats a value, and
 * "answered, not showing" beats "not answered" — because it is the truth, and
 * because the two used to read the same.
 */
function readoutFor(
  concealed: boolean,
  na: boolean,
  seated: number | null,
  sealed: boolean,
): string {
  if (concealed) return "Hidden until reveal";
  if (na) return "Doesn't apply";
  if (seated === null) return sealed ? "Answered — hidden until reveal" : "Not rated";
  return formatStars(seated) ?? "Not rated";
}

/**
 * Everything the markup reads, derived in one place.
 *
 * Concealment drops the value here rather than in CSS: it otherwise reaches the
 * native input's `value`, its `aria-valuetext` and the fill width, all of which
 * are readable by the person the rating is hidden from. Two different concealed
 * ratings must produce an identical view.
 */
export function starView(input: StarViewInput): StarView {
  const {
    value,
    na = false,
    blind = false,
    isOwn = true,
    revealed = false,
    committed = false,
  } = input;

  const concealed = blind && !isOwn && !revealed;
  const seated = concealed ? null : value;
  // Only where there is genuinely nothing to draw. A rater who has started a new
  // answer is looking at that answer, and the old one is no longer what the
  // control is about.
  const sealed = committed && !concealed && !na && seated === null;

  return {
    concealed,
    sealed,
    value: seated,
    inputValue: seated ?? STAR_MIN,
    unset: seated === null || na,
    readout: readoutFor(concealed, na, seated, sealed),
    // A width, not a position: five stars filled is the whole row. The rail on
    // the results screen maps the same value onto an *axis* instead, which is a
    // different sum — see `starFraction`.
    fill: seated === null || na ? 0 : (seated / STAR_MAX) * 100,
  };
}

// ---------------------------------------------------------------------------
// Commit planning — the one place that decides what a gesture may say
// ---------------------------------------------------------------------------

export interface CommitPlan {
  /** Emit `onNaChange(false)`; only ever true when N/A is really being cleared. */
  clearNa: boolean;
  /** The half-star value for `onChange`. */
  value: number;
}

/**
 * What a gesture landing on `target` should emit, or null for nothing.
 *
 * Re-stating the rating already seated is not a change and must not become a
 * write. Re-stating it while N/A is set *is* a change, because the answer stops
 * being "doesn't apply" — the one case where a value is emitted without moving.
 */
export function planCommit(current: number | null, na: boolean, target: number): CommitPlan | null {
  const clamped = clampStar(target);
  if (clamped === current && !na) return null;
  return { clearNa: na, value: clamped };
}

/**
 * Which star a pointer at `offsetX` (from the row's left edge) is on, 1..5.
 *
 * A whole star is the target, not a half of one. Halves are reached by tapping
 * the same star again — see `tapValue` — which is what lets every target on this
 * row be a full 44px instead of the 22px a position-decides-the-half grid would
 * leave. That grid is what this replaced: aiming at "the left bit" of a star is
 * a thing you can do with a mouse and cannot reliably do with a thumb, in a dim
 * room, on a phone being handed around.
 */
export function starIndexAt(offsetX: number, width: number): number {
  if (width <= 0) return 1;
  const raw = Math.ceil((offsetX / width) * STAR_COUNT);
  return Math.min(Math.max(raw, 1), STAR_COUNT);
}

/**
 * What a tap on `star` should set, given what is seated now.
 *
 * A star you are not on becomes a full star. A star you are already on toggles:
 * full, then half, then full again, so the same target cycles rather than the
 * rating depending on where inside the star the finger landed. Tapping around
 * the row therefore always gives whole stars, and a half is a deliberate second
 * tap on the one you meant.
 */
export function tapValue(current: number | null, star: number): number {
  const full = fullValue(star);
  return current === full ? full - 1 : full;
}

const NAV_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

/** One star, drawn twice: once as the outline, once inside the fill clip. */
function StarGlyph(props: { filled: boolean }): ReactNode {
  return (
    <svg className="pb-stars__glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        className={props.filled ? "pb-stars__path pb-stars__path--on" : "pb-stars__path"}
        d="M12 2.6l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.4l-5.8 3.06 1.11-6.46-4.7-4.58 6.49-.94z"
      />
    </svg>
  );
}

/**
 * Both rows of glyphs, built once. Neither depends on a prop — the fill is drawn
 * by clipping the filled row, not by re-rendering it — and `Stars` re-renders on
 * every half-star a drag crosses, so rebuilding twenty element descriptors per
 * pointer move bought nothing.
 */
const OUTLINE_GLYPHS = Array.from({ length: STAR_COUNT }, (_, i) => (
  <StarGlyph key={i} filled={false} />
));
const FILLED_GLYPHS = Array.from({ length: STAR_COUNT }, (_, i) => <StarGlyph key={i} filled />);

export function Stars(props: StarsProps): ReactNode {
  const {
    value,
    onChange,
    aspectName,
    playerName,
    blind = false,
    isOwn = true,
    revealed = false,
    committed = false,
    disabled = false,
    locked = false,
    onArm,
    naEnabled = false,
    na = false,
    onNaChange,
  } = props;

  const id = useId();
  const rowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Set while a pointer gesture owns the row, so moves are tracked. */
  const dragging = useRef(false);

  const view = starView({ value, na, blind, isOwn, revealed, committed });
  const { concealed } = view;

  const commit = useCallback(
    (target: number) => {
      if (disabled || concealed) return;
      // Before `planCommit`, not after: a gesture that lands on the value already
      // showing plans to nothing, and arming has to happen on that one too or the
      // control cannot be picked up without first being moved off its own figure.
      if (locked) {
        onArm?.();
        return;
      }
      const plan = planCommit(view.value, na, target);
      if (!plan) return;
      // N/A first: the consumer merges from a value it captured this render, so
      // clearing after the value would overwrite the value with the stale null.
      if (plan.clearNa) onNaChange?.(false);
      onChange?.(plan.value);
    },
    [disabled, concealed, locked, onArm, view.value, na, onNaChange, onChange],
  );

  /** Which star a pointer event is over, in the row's coordinates. */
  const starOf = useCallback((clientX: number): number | null => {
    const row = rowRef.current;
    if (!row) return null;
    const box = row.getBoundingClientRect();
    return starIndexAt(clientX - box.left, box.width);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (disabled || concealed) return;
      const star = starOf(event.clientX);
      if (star === null) return;
      dragging.current = true;
      // Capture so a finger that slides off the row keeps steering it, and so
      // the release always arrives here rather than at whatever it slid onto.
      event.currentTarget.setPointerCapture?.(event.pointerId);
      // The row is the visual control but the input is the focusable one; a tap
      // has to leave focus somewhere the keyboard can carry on from.
      inputRef.current?.focus();
      commit(tapValue(view.value, star));
    },
    [disabled, concealed, starOf, commit, view.value],
  );

  /**
   * A sweep sets whole stars.
   *
   * Only a press toggles, deliberately: dragging back and forth across one star
   * would otherwise flicker between four and four and a half under the finger,
   * and a gesture that lands where it started would leave a different rating
   * than the one it picked up. Coarse with the drag, exact with a second tap.
   */
  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragging.current || disabled || concealed) return;
      const star = starOf(event.clientX);
      if (star !== null) commit(fullValue(star));
    },
    [disabled, concealed, starOf, commit],
  );

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  /** The keyboard path. The native range raises this; pointers never do. */
  const onInput = useCallback(
    (event: ReactChangeEvent<HTMLInputElement>) => {
      commit(Number(event.currentTarget.value));
    },
    [commit],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      // A locked control is showing a value, so `view.unset` is false and the
      // early return below would let the native range move it. Arm on any nav
      // key instead: the keyboard gets the same two-step the thumb does.
      if (locked && NAV_KEYS.has(event.key)) {
        event.preventDefault();
        onArm?.();
        return;
      }
      if (!view.unset || !NAV_KEYS.has(event.key)) return;
      // From unset, a nav key must not land on whatever the hidden internal
      // value happens to be. Enter the ladder from the end the key implies.
      event.preventDefault();
      const fromEnd = event.key === "ArrowLeft" || event.key === "ArrowDown" || event.key === "End";
      commit(fromEnd ? STAR_MIN : STAR_MAX);
    },
    [view.unset, commit, locked, onArm],
  );

  const label = `${playerName ? `${playerName}, ` : ""}${aspectName} rating`;

  return (
    <div
      className="pb-stars"
      data-unset={view.unset || concealed ? "true" : "false"}
      data-concealed={concealed ? "true" : "false"}
      data-sealed={view.sealed ? "true" : "false"}
      data-locked={locked ? "true" : "false"}
      data-disabled={disabled ? "true" : "false"}
    >
      <div className="pb-stars__head">
        <span className="pb-stars__aspect" id={`${id}-aspect`}>
          {playerName ? `${playerName} · ${aspectName}` : aspectName}
        </span>
        <span className="pb-stars__readout">{view.readout}</span>
      </div>

      <div
        className="pb-stars__row"
        ref={rowRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="pb-stars__glyphs" aria-hidden="true">
          {OUTLINE_GLYPHS}
        </div>

        {/*
          The filled row, clipped to the rating. Clipping a duplicate of the
          same glyphs is what makes a half star exactly half: the alternative,
          a per-star "half" glyph, has to decide what half of a star point
          looks like and gets it wrong at every size but one.
        */}
        <div className="pb-stars__fill" style={{ width: `${view.fill}%` }} aria-hidden="true">
          <div className="pb-stars__glyphs">{FILLED_GLYPHS}</div>
        </div>

        <input
          ref={inputRef}
          className="pb-stars__input"
          type="range"
          min={STAR_MIN}
          max={STAR_MAX}
          step={1}
          value={view.inputValue}
          disabled={disabled || concealed}
          aria-label={label}
          aria-valuetext={
            view.concealed || view.sealed
              ? view.readout
              : starsAnnouncement(view.unset ? null : view.value)
          }
          onChange={onInput}
          onKeyDown={onKeyDown}
        />
      </div>

      {naEnabled && !concealed && (
        <label className="pb-stars__na">
          <input
            type="checkbox"
            checked={na}
            // Locked as well: "doesn't apply" over a worked-out figure is the
            // same accidental overwrite the stars are gated against, and one
            // click rather than two would make the checkbox the way round.
            disabled={disabled || locked}
            onChange={(e) => {
              const next = e.currentTarget.checked;
              if (next !== na) onNaChange?.(next);
            }}
          />
          Doesn&rsquo;t apply
        </label>
      )}
    </div>
  );
}

/** Re-exported so consumers reading a rating do not import two modules. */
export { starsOf };
